import { TicketOption, TicketOptionWithQuantity, Translations } from '@repo/types';
import { QuantityError, QuantityInputItem } from './types';

/**
 * Handles both string names and function-based names (for pluralization).
 *
 * @param ticket - Ticket to get name from
 * @param translations - Translation object (for fallbacks)
 * @returns Display name as string
 */
export const getTicketName = (
    ticket: TicketOptionWithQuantity,
    _translations: Translations,
): string => {
    // ticket.name can be either a string or a function for pluralization
    const name = ticket.name as string | ((num: number) => string);

    if (typeof name === 'function') {
        return name(ticket.quantity);
    }
    return name || '';
};

/**
 * Returns formatted age range like "(Age 18-65)" or undefined if incomplete.
 *
 * @param ticket - Ticket with age range data
 * @param translations - Translation object
 * @returns Formatted age range or undefined
 */
export const getAgeRangeText = (
    ticket: TicketOptionWithQuantity,
    translations: Translations,
): string | undefined => {
    // Both fromAge and toAge must be present (not null/undefined)
    if (typeof ticket.fromAge === 'number' && typeof ticket.toAge === 'number') {
        return `(${capitalize(translations.age)} ${ticket.fromAge}-${ticket.toAge})`;
    }

    return undefined;
};

/**
 * Finds matching error in errors array and returns translated message.
 * Suppresses capacity errors when quantity is zero.
 *
 * @param ticketId - Ticket category ID to find error for
 * @param errors - Array of validation errors
 * @param ticket - Ticket option (to check quantity for suppression logic)
 * @param translations - Translation object
 * @returns Error message or undefined
 */
export const getErrorForTicket = (
    ticketId: string,
    errors: QuantityError[],
    ticket: TicketOptionWithQuantity,
    translations: Translations,
): string | undefined => {
    const error = errors.find((e) => e.id === ticketId || e.id === 'collection');

    if (!error) {
        return undefined;
    }

    // Suppress capacity errors when quantity is zero
    if (error.error === 'capacity' && ticket.quantity === 0) {
        return undefined;
    }

    // Don't show collection-level minEntrants errors on optional tickets
    // Optional tickets (minEntrants: null) shouldn't appear required due to collection constraints
    if (error.id === 'collection' && error.error === 'minEntrants' && ticket.minEntrants === null) {
        return undefined;
    }

    return translateError(error, translations);
};

/**
 * Translates a QuantityError to a user-facing localized message.
 * Uses the i18n translation system for proper localization.
 */
function translateError(error: QuantityError, translations: Translations): string {
    if (error.error === 'minEntrants') {
        return translations.too_few_travelers_quantity.parsed(error.value);
    }

    if (error.error === 'capacity') {
        return translations.number_of_travelers_exceeded.parsed(error.value);
    }

    return translations.unknown;
}

export function capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

const getAgeRangeTextFromTicket = (
    ticket: TicketOption,
    translations: Translations,
): string | undefined => {
    if (typeof ticket.fromAge === 'number' && typeof ticket.toAge === 'number') {
        return `(${capitalize(translations.age)} ${ticket.fromAge}-${ticket.toAge})`;
    }
    return undefined;
};

/**
 * Transform a TicketOption to a default QuantityInputItem
 *
 * Creates a placeholder item for display purposes.
 * Used for showing ALL product tickets, including unavailable ones.
 * The item is disabled by default - available items will replace it during merge.
 *
 * @param ticket - TicketOption from product.ticketOptions
 * @param translations - Translation object
 * @returns QuantityInputItem with display info, marked as disabled
 */
export const toDefaultInputItem = (
    ticket: TicketOption,
    translations: Translations,
): QuantityInputItem => {
    const name = ticket.name as string | ((num: number) => string);
    const label = typeof name === 'function' ? name(0) : name || '';

    return {
        id: ticket.ticketCategoryId,
        label: capitalize(label),
        value: 0,
        min: 0,
        max: 0,
        subText: getAgeRangeTextFromTicket(ticket, translations),
        disabled: true,
    };
};

export const toDefaultInputItems = (
    tickets: readonly TicketOption[],
    translations: Translations,
): QuantityInputItem[] => {
    return tickets.map((ticket) => toDefaultInputItem(ticket, translations));
};
